/**
 * Servicio de Logging Estructurado - Sistema VALIDA
 *
 * Política OBLIGATORIA: Todo código debe usar este logger en lugar de console.log
 * Implementa logging estructurado con contexto, niveles y configuración por entorno
 *
 * @version 1.0.0
 * @author Equipo VALIDA
 * @date 2025-11-28
 */

// Configuración del logger
const LOG_CONFIG = {
  enabled: true,
  level: process.env.NODE_ENV === 'development' ? 'debug' : 'info',
  showTimestamp: true,
  showComponent: true,
  showUser: true,
  maxMessageLength: 500,
  version: '1.0.0'
};

// Niveles de log (ordenados por severidad)
const LOG_LEVELS = {
  trace: 0,
  debug: 1,
  info: 2,
  warn: 3,
  error: 4,
  critical: 5
};

// Colores para consola (solo desarrollo)
const LOG_COLORS = {
  trace: '#9CA3AF',    // gray
  debug: '#3B82F6',    // blue
  info: '#10B981',     // green
  warn: '#F59E0B',     // yellow
  error: '#EF4444',    // red
  critical: '#7C2D12'  // dark red
};

/**
 * Clase principal del Logger
 */
class Logger {
  constructor() {
    this.config = LOG_CONFIG;
    this.userContext = this.getUserContext();
  }

  /**
   * Obtiene el contexto del usuario actual
   */
  getUserContext() {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        // Decodificar JWT básico para obtener user info
        const payload = JSON.parse(atob(token.split('.')[1]));
        return {
          userId: payload.userId || payload.sub,
          username: payload.username || payload.preferred_username,
          role: payload.role
        };
      }
    } catch (error) {
      // Silenciar errores de parsing de token
    }
    return null;
  }

  /**
   * Actualiza el contexto de usuario (llamar después de login/logout)
   */
  updateUserContext() {
    this.userContext = this.getUserContext();
  }

  /**
   * Verifica si un nivel de log está habilitado
   */
  isLevelEnabled(level) {
    return LOG_LEVELS[level] >= LOG_LEVELS[this.config.level];
  }

  /**
   * Formatea el mensaje de log con contexto
   */
  formatMessage(level, message, context = {}) {
    const timestamp = this.config.showTimestamp
      ? new Date().toISOString()
      : null;

    const component = this.config.showComponent && context.component
      ? `[${context.component}]`
      : '';

    const user = this.config.showUser && this.userContext
      ? `[${this.userContext.username || this.userContext.userId}]`
      : '';

    // Limitar longitud del mensaje
    const truncatedMessage = message.length > this.config.maxMessageLength
      ? message.substring(0, this.config.maxMessageLength) + '...'
      : message;

    const parts = [timestamp, level.toUpperCase(), component, user, truncatedMessage].filter(Boolean);
    return parts.join(' ');
  }

  /**
   * Loggea un mensaje con el nivel especificado
   */
  log(level, message, context = {}) {
    if (!this.config.enabled || !this.isLevelEnabled(level)) {
      return;
    }

    const formattedMessage = this.formatMessage(level, message, context);
    const logData = {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
      user: this.userContext,
      version: this.config.version
    };

    // Log en consola con colores (solo desarrollo)
    if (process.env.NODE_ENV === 'development') {
      const color = LOG_COLORS[level] || LOG_COLORS.info;
      console.log(`%c${formattedMessage}`, `color: ${color}; font-weight: bold;`);

      // Mostrar objeto de contexto si existe
      if (Object.keys(context).length > 0) {
        console.log('Context:', context);
      }
    }

    // En producción, podríamos enviar a un servicio de logging
    // this.sendToLoggingService(logData);

    // Almacenar en sessionStorage para debugging (últimos 50 logs)
    this.storeLogInSession(logData);
  }

  /**
   * Almacena log en sessionStorage para debugging
   */
  storeLogInSession(logData) {
    try {
      const logs = JSON.parse(sessionStorage.getItem('valida_logs') || '[]');
      logs.push(logData);

      // Mantener solo los últimos 50 logs
      if (logs.length > 50) {
        logs.shift();
      }

      sessionStorage.setItem('valida_logs', JSON.stringify(logs));
    } catch (error) {
      // Silenciar errores de sessionStorage
    }
  }

  /**
   * Obtiene logs almacenados para debugging
   */
  getStoredLogs() {
    try {
      return JSON.parse(sessionStorage.getItem('valida_logs') || '[]');
    } catch (error) {
      return [];
    }
  }

  /**
   * Limpia logs almacenados
   */
  clearStoredLogs() {
    sessionStorage.removeItem('valida_logs');
  }

  // Métodos de conveniencia para cada nivel

  /**
   * Log de traza (muy detallado)
   */
  trace(message, context = {}) {
    this.log('trace', message, context);
  }

  /**
   * Log de debug (desarrollo)
   */
  debug(message, context = {}) {
    this.log('debug', message, context);
  }

  /**
   * Log informativo (operaciones normales)
   */
  info(message, context = {}) {
    this.log('info', message, context);
  }

  /**
   * Log de advertencia (casos no críticos)
   */
  warn(message, context = {}) {
    this.log('warn', message, context);
  }

  /**
   * Log de error (errores recuperables)
   */
  error(message, context = {}) {
    this.log('error', message, context);
  }

  /**
   * Log crítico (errores que requieren atención inmediata)
   */
  critical(message, context = {}) {
    this.log('critical', message, context);
  }

  // Métodos especializados

  /**
   * Log de operación API
   */
  apiCall(method, endpoint, status, duration, context = {}) {
    const level = status >= 400 ? 'error' : 'info';
    const message = `API ${method} ${endpoint} - ${status} (${duration}ms)`;
    this.log(level, message, { ...context, apiMethod: method, apiEndpoint: endpoint, apiStatus: status, apiDuration: duration });
  }

  /**
   * Log de error de red
   */
  networkError(endpoint, error, context = {}) {
    const message = `Network error: ${endpoint} - ${error.message}`;
    this.log('error', message, { ...context, endpoint, error: error.message, isNetworkError: true });
  }

  /**
   * Log de cambio de componente React
   */
  componentLifecycle(component, action, context = {}) {
    const message = `Component ${component} - ${action}`;
    this.log('debug', message, { ...context, component, lifecycle: action });
  }

  /**
   * Log de acción de usuario
   */
  userAction(action, target, context = {}) {
    const message = `User action: ${action} on ${target}`;
    this.log('info', message, { ...context, userAction: action, target });
  }

  /**
   * Log de validación
   */
  validation(field, isValid, message, context = {}) {
    const level = isValid ? 'debug' : 'warn';
    const status = isValid ? 'passed' : 'failed';
    const logMessage = `Validation ${status} for ${field}: ${message}`;
    this.log(level, logMessage, { ...context, field, isValid, validationMessage: message });
  }
}

// Instancia singleton del logger
const logger = new Logger();

// Exportar logger y configuración
export { logger, LOG_CONFIG };
export default logger;